import React, {
  ReactNode,
  ChangeEvent,
  useContext,
  useEffect,
  useState,
  useMemo,
  FormEvent,
} from 'react';
import {
  NavigateFunction,
  useFetcher,
  useNavigate,
  useLoaderData,
  ActionFunction,
} from 'react-router';
import { Box, Button, Grid, Typography } from '@mui/material';
import {
  PhoneType,
  User,
  UserData,
  TraxeroActionResponse,
  Contact,
} from 'types';
import { ROUTES, routeWithParams } from 'router';
import { ToastContext, TOAST_TYPE } from 'context/toastContext';
import { Notifications } from 'components/inputs/NotificationPrefs';
import { AppContext, ACTION_TYPE } from 'context/appContext';
import { useTranslation } from 'react-i18next';

import { ValidatorForm } from 'react-material-ui-form-validator';
import DetailsHeader from 'components/layout/DetailsHeader';
import FormFrame from 'components/layout/FormFrame';
import SettingsSection from 'components/layout/SettingsSection';
import AuturaTextField from 'components/inputs/AuturaTextField';
import AuturaPhoneField from 'components/inputs/AuturaPhoneField';
import { ScrollableFormContainer } from 'components/layout/ScrollableContainer';
import { updateUser, getUser, checkDuplicateAccount } from 'api/users';
import AuthService from 'utils/AuthService';
import { getBlankContact } from 'utils/emptyEntities';
import FormHeaderBar from 'components/layout/FormHeaderBar';
import SignaturePad from 'components/inputs/SignaturePad';
import { useFetcherKey } from 'hooks/useFetcherKey';

export interface UserProfileLoaderData {
  _user: Promise<User>;
}

export const userProfileLoader = (): UserProfileLoaderData => {
  const userID = AuthService.GetUserID();
  return { _user: getUser(userID) };
};

enum UserProfileActionHandler {
  UPDATE = 'UPDATE',
  CHECK_DUPLICATE_ACCOUNT = 'CHECK_DUPLICATE_ACCOUNT',
}

const UserProfile = (): ReactNode => {
  const { t } = useTranslation();
  const navigate: NavigateFunction = useNavigate();
  const { _user } = useLoaderData() as UserProfileLoaderData;

  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const displayToast = useContext(ToastContext);
  const { state, dispatch } = useContext(AppContext);
  const [user, setUser] = useState<User>(state.user as User);
  const [userData, setUserData] = useState<UserData>(user.userData);
  const [contact, setContact] = useState<Contact>(
    user.contact ?? getBlankContact(state.tenant.id),
  );
  const [lockUI, setLockUI] = useState<boolean>(false);

  useEffect(() => {
    dispatch({
      type: ACTION_TYPE.SET_HEADER_TEXT,
      payload: t('tabMenu.user-profile'),
    });
  }, [dispatch, t]);

  useEffect(() => {
    const awaitLoaderData = async () => {
      const u = await _user;
      setUser(u);
      setUserData(u.userData);
      setContact(u.contact ?? getBlankContact(state.tenant.id));
      dispatch({ type: ACTION_TYPE.SET_USER, payload: u });
    };
    awaitLoaderData();
  }, [_user]);

  useEffect(() => {
    if (fetcher.data) {
      const response =
        fetcher.data as TraxeroActionResponse<UserProfileActionHandler>;

      if (response.status === 200) {
        const updatedUser: User = {
          ...user,
          contact: {
            ...contact,
          },
          userData,
        };
        dispatch({ type: ACTION_TYPE.SET_USER, payload: updatedUser });
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: t('update_success', {
            ns: 'toasts',
            name: t('user.label', { ns: 'entities' }),
          }),
        });
      } else {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: t('update_error', {
            ns: 'toasts',
            name: t('user.label', { ns: 'entities' }),
            error: response.statusText,
          }),
        });
      }
    }
  }, [fetcher.data, user, userData, contact]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    switch (name) {
      case 'firstName':
        setUser({ ...user, firstName: value });
        break;
      case 'lastName':
        setUser({ ...user, lastName: value });
        break;
      case 'email':
        setUser({ ...user, email: value });
        break;
      case 'contact.description':
        setContact({ ...contact, description: value });
        break;
      case 'contact.website':
        setContact({ ...contact, website: value });
        break;
      case 'contact.secondaryEmail':
        setContact({ ...contact, secondaryEmail: value });
        break;
    }
  };

  const handleSubmitUserProfile = (e: FormEvent<Element>) => {
    e.preventDefault();
    const userBody: User = {
      ...user,
      userData,
      contact,
    };
    fetcher.submit(
      {
        body: JSON.stringify(userBody),
        userID: user.id,
        email: user?.email || '',
        token: state.user.jwtToken || '',
        handler:
          state.user.email !== user?.email
            ? UserProfileActionHandler.CHECK_DUPLICATE_ACCOUNT
            : UserProfileActionHandler.UPDATE,
      },
      {
        method: 'PUT',
        encType: 'application/json',
      },
    );
  };

  const handleCancelAction = () => {
    setUser(state.user as User);
    setContact(state.user.contact as Contact);
    setUserData(state.user.userData as UserData);
  };

  const handleResetPassword = () => {
    user.email
      ? navigate(
          routeWithParams(ROUTES.CHANGE_PASSWORD, [
            state.tenant?.data?.companyPath || '',
          ]),
        )
      : navigate(
          window.CLIENT_BASE_URL +
            routeWithParams(ROUTES.REGISTRATION, [
              state.tenant?.data?.companyPath || '',
              state.user.jwtToken || '',
            ]),
        );
  };

  const hasMobilePhone = useMemo(() => {
    return (
      contact.primaryPhoneType === PhoneType.Mobile ||
      contact.secondaryPhoneType === PhoneType.Mobile ||
      contact.tertiaryPhoneType === PhoneType.Mobile
    );
  }, [
    contact.primaryPhoneType,
    contact.secondaryPhoneType,
    contact.tertiaryPhoneType,
  ]);

  return (
    <ValidatorForm onSubmit={handleSubmitUserProfile}>
      <FormHeaderBar />
      <FormFrame loading={fetcher.state !== 'idle'}>
        <DetailsHeader
          title={
            <Typography
              variant={'h2'}
              textTransform={'lowercase'}
              component={'span'}
            >
              {user?.email || ''}
            </Typography>
          }
          onCancel={handleCancelAction}
          submitLabel={t('buttons.update', { name: t('settings') })}
          disableSubmit={lockUI}
          disableCancel={lockUI}
        />
        <ScrollableFormContainer maxHeight={`calc(100vh - 326px)`}>
          <SettingsSection
            title={t('subheadings.personal_information', {
              ns: 'userSettings',
            })}
            divider={false}
            subtitleError={!user.email}
            subtitle={
              !user.email ? t('helpers.no_email', { ns: 'userSettings' }) : ''
            }
          >
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='userID'
                  label={t('user.id', { ns: 'entities' })}
                  value={user.id}
                  readonly
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  required
                  name='firstName'
                  value={user.firstName}
                  label={t('user.firstName', { ns: 'entities' })}
                  inputSx={{ textTransform: 'capitalize' }}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  required
                  name='lastName'
                  value={user.lastName}
                  label={t('user.lastName', { ns: 'entities' })}
                  inputSx={{ textTransform: 'capitalize' }}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  required
                  name='email'
                  value={user.email || ''}
                  label={t('user.email', { ns: 'entities' })}
                  onChange={handleChange}
                  validators={['isEmail']}
                  errorMessages={[
                    t('errors.invalidError', { name: t('email') }),
                  ]}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaPhoneField
                  label={t('user.contact.primaryPhone', { ns: 'entities' })}
                  name={'contact.primaryPhone'}
                  typeFieldName={'contact.primaryPhoneType'}
                  typeFieldLabel={t('user.contact.primaryPhoneType', {
                    ns: 'entities',
                  })}
                  phoneNumber={contact.primaryPhone || ''}
                  setPhoneNumber={(value) =>
                    setContact({ ...contact, primaryPhone: value })
                  }
                  phoneType={contact.primaryPhoneType}
                  setPhoneType={(value) =>
                    setContact({ ...contact, primaryPhoneType: value })
                  }
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaPhoneField
                  label={t('user.contact.secondaryPhone', { ns: 'entities' })}
                  name={'contact.secondaryPhone'}
                  typeFieldName={'contact.secondaryPhoneType'}
                  typeFieldLabel={t('user.contact.secondaryPhoneType', {
                    ns: 'entities',
                  })}
                  phoneNumber={contact.secondaryPhone || ''}
                  setPhoneNumber={(value) =>
                    setContact({ ...contact, secondaryPhone: value })
                  }
                  phoneType={contact.secondaryPhoneType}
                  setPhoneType={(value) =>
                    setContact({ ...contact, secondaryPhoneType: value })
                  }
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaPhoneField
                  label={t('user.contact.tertiaryPhone', { ns: 'entities' })}
                  name={'contact.tertiaryPhone'}
                  typeFieldName={'contact.tertiaryPhoneType'}
                  typeFieldLabel={t('user.contact.tertiaryPhoneType', {
                    ns: 'entities',
                  })}
                  phoneNumber={contact.tertiaryPhone || ''}
                  setPhoneNumber={(value) =>
                    setContact({ ...contact, tertiaryPhone: value })
                  }
                  phoneType={contact.tertiaryPhoneType}
                  setPhoneType={(value) =>
                    setContact({ ...contact, tertiaryPhoneType: value })
                  }
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('user.contact.secondaryEmail', { ns: 'entities' })}
                  name={'contact.secondaryEmail'}
                  value={contact.secondaryEmail || ''}
                  onChange={handleChange}
                  validators={['isEmail']}
                  errorMessages={[
                    t('errors.invalidError', { name: t('email') }),
                  ]}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('user.contact.description', { ns: 'entities' })}
                  name={'contact.description'}
                  value={contact.description || ''}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('user.contact.website', { ns: 'entities' })}
                  name={'contact.website'}
                  value={contact.website || ''}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <Notifications
                  readonly={false}
                  data={user.userData}
                  setUserData={setUserData}
                  hasEmailAddress={!!user.email}
                  hasMobilePhone={hasMobilePhone}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    height: '100%',
                    justifyContent: 'center',
                    alignContent: 'center',
                    width: '100%',
                  }}
                >
                  <Button
                    sx={{
                      height: 56,
                    }}
                    variant={'outlined'}
                    onClick={handleResetPassword}
                    color={'primary'}
                    fullWidth
                    disabled={!user.email}
                  >
                    {t('labels.reset_password', { ns: 'userSettings' })}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('subheadings.signature', {
              ns: 'userSettings',
            })}
            divider={false}
          >
            <Box
              sx={{ width: { xs: '100%', lg: '50%' }, pr: { xs: 0, lg: 1.5 } }}
            >
              <SignaturePad setUILock={setLockUI} />
            </Box>
          </SettingsSection>
        </ScrollableFormContainer>
      </FormFrame>
    </ValidatorForm>
  );
};

export const userProfileAction: ActionFunction = async ({
  request,
}): Promise<TraxeroActionResponse<UserProfileActionHandler>> => {
  const { body, userID, handler, email, token } = await request.json();

  let res: Response;
  try {
    switch (handler) {
      case UserProfileActionHandler.UPDATE:
        res = await updateUser({ body, userID });
        break;
      case UserProfileActionHandler.CHECK_DUPLICATE_ACCOUNT: {
        res = await checkDuplicateAccount({ email, token });
        const response = await res.json();
        if (response.activeAccount) {
          return {
            handler,
            status: res.status,
            statusText: 'Email Already Exists, please choose a different email',
          };
        }
        res = await updateUser({ body, userID });
        break;
      }
      default:
        res = new Response('Unknown handler', {
          status: 404,
          statusText: 'Unknown Handler',
        });
    }
  } catch (error) {
    res = new Response('', { status: 500, statusText: 'Network Error' });
  }

  return {
    handler,
    status: res.status,
    statusText: res.statusText,
  };
};

export default UserProfile;
